import {
  AssignmentLinkCellRenderer,
  CampaignLinkCellRenderer,
} from "components/DataGrid/components";
import { BrandLinkCellRenderer } from "components/DataGrid/components/LinkCellRenderers/BrandLinkCellRenderer";
import { CreatorLinkCellRenderer } from "components/DataGrid/components/LinkCellRenderers/CreatorLinkCellRenderer";
import BoolCellRenderer from "components/DataGrid/customRenderers/BoolCellRenderer";
import { ApproveMilestoneCellRenderer } from "../components/ApproveMilestoneCellRenderer";
import { ApprovalStatusCellRenderer } from "../components/ApprovalStatusCellRenderer";

/**
 * Creates approval column definitions with permission-based conditional columns
 */
const createApprovalsColDefs = (canApprovePayment: boolean) => [
  // Hidden ID fields for data integrity
  {
    headerName: "_id",
    field: "_id",
    type: "text",
    hide: true,
    sortable: false,
  },
  {
    headerName: "Assignment ID",
    field: "assignment_id",
    dataSource: "assignment",
    type: "text",
    hide: true,
    lockVisible: true,
    sortable: false,
  },
  {
    headerName: "Creator ID",
    field: "user_id._id",
    dataSource: "creator",
    type: "text",
    hide: true,
    lockVisible: true,
    sortable: false,
  },
  {
    headerName: "Campaign ID",
    field: "campaign_id._id",
    dataSource: "campaign",
    type: "text",
    hide: true,
    lockVisible: true,
    sortable: false,
  },
  {
    headerName: "Brand ID",
    field: "brand_id._id",
    dataSource: "brand._id",
    type: "text",
    hide: true,
    lockVisible: true,
    sortable: false,
  },

  // Approval status indicator (first visible column)
  {
    headerName: "Approval Status",
    field: "isPaymentApproved",
    type: "text",
    sortable: false,
    initialWidth: 140,
    cellRenderer: ApprovalStatusCellRenderer,
  },

  // Brand column
  {
    headerName: "Brand",
    field: "brand_id._id",
    dataSource: "brand._id",
    cellRenderer: BrandLinkCellRenderer,
    type: "text",
    sortable: false,
    filterValueGetter: (params: any) => {
      if (!params.data?.brand_id?.name) {
        return "";
      }
      return `${params.data?.brand_id?.name} ${params.data?.brand_id?._id}`;
    },
    valueFormatter: (params: any) => {
      return params.data?.brand_id?.name || "";
    },
  },

  // Campaign columns
  {
    headerName: "Campaign",
    field: "campaign_id._id",
    dataSource: "campaign",
    type: "text",
    cellRenderer: CampaignLinkCellRenderer,
    sortable: false,
    filterValueGetter: (params: any) => {
      if (!params.data?.campaign_id?.name) {
        return "";
      }
      return `${params.data?.campaign_id?.name} ${params.data?.campaign_id?._id}`;
    },
    valueFormatter: (params: any) => {
      return params.data?.campaign_id?.name || "";
    },
  },
  {
    headerName: "Campaign Short ID",
    field: "campaign_id._id",
    dataSource: "campaign",
    type: "text",
    cellRenderer: CampaignLinkCellRenderer,
    cellRendererParams: {
      linkTextKey: "campaign_id.shortId",
    },
    sortable: false,
    filterValueGetter: (params: any) => {
      return params.data?.campaign_id?.shortId;
    },
    valueFormatter: (params: any) => {
      return params.data?.campaign_id?.shortId || "";
    },
  },

  // Creator information
  {
    headerName: "Creator",
    field: "user_id._id",
    dataSource: "creator",
    cellRenderer: CreatorLinkCellRenderer,
    type: "text",
    sortable: false,
    initialWidth: 150,
    filterValueGetter: (params: any) => {
      if (!params.data?.user_id?.name) {
        return "";
      }
      return `${params.data?.user_id?.name} ${params.data?.user_id?._id}`;
    },
    valueFormatter: (params: any) => {
      return params.data?.user_id?.name || "";
    },
  },

  // Assignment information
  {
    headerName: "Assignment",
    field: "assignment_id",
    dataSource: "assignment",
    cellRenderer: AssignmentLinkCellRenderer,
    type: "text",
    sortable: false,
    initialWidth: 200,
    filterValueGetter: (params: any) => {
      // Makes the assignment's creator name, campaign name, and assignment ID searchable
      if (!params.data?.user_id?.name || !params.data?.campaign_id?.name) {
        return "";
      }
      return `${params.data?.user_id?.name} ${params.data?.campaign_id?.name} ${params.data?.assignment_id}`;
    },
    valueFormatter: (params: any) => {
      return `${params.data?.user_id?.name || "unknown"} <> ${params.data?.campaign_id?.name || "unknown"}`;
    },
  },

  // Milestone details
  {
    headerName: "Milestone Name",
    field: "name",
    type: "text",
    sortable: false,
    initialWidth: 200,
  },

  {
    headerName: "Status/Notes",
    field: "status",
    type: "text",
    sortable: false,
    initialWidth: 150,
  },
  {
    headerName: "Priority",
    field: "priority",
    type: "text",
    sortable: false,
    initialWidth: 100,
    valueFormatter: (params: any) => {
      return params.value?.charAt(0)?.toUpperCase() + params.value?.slice(1);
    },
  },

  // Financial information
  {
    headerName: "Rate",
    field: "rate",
    type: "currency",
    sortable: false,
    initialWidth: 120,
  },
  {
    headerName: "Hold Balance",
    field: "holdBalance",
    type: "currency",
    sortable: false,
    initialWidth: 130,
  },
  {
    headerName: "Paid Balance",
    field: "paidBalance",
    type: "currency",
    sortable: false,
    initialWidth: 130,
  },

  // Status fields
  {
    headerName: "Complete?",
    field: "isComplete",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
    sortable: false,
    initialWidth: 100,
  },
  {
    headerName: "Release Payment?",
    field: "releasePayment",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
    sortable: false,
    initialWidth: 130,
  },

  {
    headerName: "Actions",
    field: "isPaymentApproved", // Tie to the field that changes
    type: "text",
    sortable: false,
    initialWidth: 140,
    cellRenderer: ApproveMilestoneCellRenderer,
    cellRendererParams: {
      canApprovePayment: canApprovePayment,
    },
    pinned: "right",
    cellClass: "text-end",
  },

  // Status fields
  {
    headerName: "Completed Date",
    field: "dateCompleted",
    type: "date",
    sortable: false,
    initialWidth: 130,
  },
  {
    headerName: "Payment Due Date",
    field: "nextPaymentDate",
    type: "date",
    sortable: false,
    initialWidth: 150,
  },

  // Department information
  {
    headerName: "Department",
    field: "campaign_id.department",
    type: "text",
    sortable: false,
    initialWidth: 120,
    valueGetter: (params: any) => {
      let text = "";
      if (params.data?.campaign_id?.department === "managed") {
        text = "Managed Services";
      }
      if (params.data?.campaign_id?.department === "talent") {
        text = "Talent";
      }
      return text;
    },
  },
];

// Export the function that creates column definitions based on permissions
export { createApprovalsColDefs };

// Also export a default version for components that don't use permissions
export const approvalsColDefs = createApprovalsColDefs(true);
